import React, { useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import AddBranchDialog from "./AddBranchDialog";
import PlanBranchTree from "./PlanBranchTree";
import GroupDefinitionsDialog from "./GroupDefinitionsDialog";
import {
    availableBranchesForParent,
    branchesToAdd,
    buildPlannerTree,
    countOpenItems,
    emptyPlannerItem,
} from "./plannerConfig";
import { QUOTE_RATE_PROFILES, formatHours, rollupPlannerQuotes } from "./plannerQuoteRates";
import { formatMoney } from "./quotePricingMath";

export default function QuotePlannerPanel({
    plannerGroups,
    setPlannerGroups,
    groupDefinitions,
    onRefreshDefinitions,
    estimateLines,
    setEstimateLines,
    rates,
    currency,
    onAddEstimateLineForGroup,
    planTitle,
}) {
    const [branchDialogOpen, setBranchDialogOpen] = useState(false);
    const [branchParent, setBranchParent] = useState(null);
    const [defsOpen, setDefsOpen] = useState(false);

    const tree = useMemo(() => buildPlannerTree(plannerGroups), [plannerGroups]);
    const openItemsTotal = useMemo(() => countOpenItems(plannerGroups), [plannerGroups]);
    const quoteRollup = useMemo(() => rollupPlannerQuotes(plannerGroups, rates), [plannerGroups, rates]);

    const definitionColors = useMemo(() => {
        const m = {};
        (groupDefinitions || []).forEach((d) => { m[d.group_key] = d.color; });
        return m;
    }, [groupDefinitions]);

    const branchOptions = useMemo(
        () => availableBranchesForParent(branchParent, groupDefinitions, plannerGroups),
        [branchParent, groupDefinitions, plannerGroups]
    );

    const openAddDialog = (parentGroup = null) => {
        setBranchParent(parentGroup);
        setBranchDialogOpen(true);
    };

    const handlePickBranch = (definition) => {
        const parentId = branchParent?.id || null;
        const toAdd = branchesToAdd(definition, groupDefinitions, parentId, plannerGroups);
        if (!toAdd.length) return;
        setPlannerGroups((rows) => [...rows, ...toAdd]);
    };

    const patchGroup = (id, patch) => {
        setPlannerGroups((rows) => rows.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    };

    const removeGroup = (id) => {
        const collectIds = (targetId) => {
            const ids = [targetId];
            plannerGroups.filter((g) => g.parent_id === targetId).forEach((c) => ids.push(...collectIds(c.id)));
            return ids;
        };
        const removeIds = new Set(collectIds(id));
        setPlannerGroups((rows) => rows.filter((g) => !removeIds.has(g.id)));
        setEstimateLines((lines) => lines.map((L) => (removeIds.has(L.group_id) ? { ...L, group_id: null } : L)));
    };

    const patchItem = (groupId, itemId, patch) => {
        setPlannerGroups((rows) =>
            rows.map((g) =>
                g.id !== groupId ? g : { ...g, items: (g.items || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
            )
        );
    };

    const addItem = (groupId) => {
        setPlannerGroups((rows) =>
            rows.map((g) =>
                g.id !== groupId ? g : { ...g, items: [...(g.items || []), emptyPlannerItem()] }
            )
        );
    };

    const removeItem = (groupId, itemId) => {
        setPlannerGroups((rows) =>
            rows.map((g) => (g.id !== groupId ? g : { ...g, items: (g.items || []).filter((it) => it.id !== itemId) }))
        );
    };

    return (
        <Box>
            <Stack spacing={1.25} sx={{ mb: 2 }}>
                <Box sx={{ maxWidth: 960 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {planTitle || "Quote plan"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {tree.length
                            ? `${tree.length} top-level branch${tree.length === 1 ? "" : "es"} · ${openItemsTotal} open needs${quoteRollup.laborHours > 0 ? ` · ${formatHours(quoteRollup.laborHours)} labor` : ""}${quoteRollup.total > 0 ? ` · Budget ${formatMoney(quoteRollup.total, currency)}` : ""}`
                            : "Start by adding branches to build your estimate structure."}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {(rates?.length ? rates : Object.values(QUOTE_RATE_PROFILES)).map((p) =>
                            `${p.label} $${p.suggested_rate ?? p.hourlyRate}/hr`
                        ).join(" · ")}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={() => setDefsOpen(true)}>
                        Templates
                    </Button>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog(null)}>
                        Add branch
                    </Button>
                </Stack>
            </Stack>

            {!tree.length ? (
                <Box
                    sx={{
                        py: 6,
                        px: 3,
                        textAlign: "center",
                        border: 1,
                        borderColor: "divider",
                        borderStyle: "dashed",
                    }}
                >
                    <Typography variant="overline" sx={{ color: "success.main", fontWeight: 800, letterSpacing: "0.2em" }}>
                        ADD
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
                        Add branches under this plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 480, mx: "auto" }}>
                        Pick Shop package for a full job breakdown, or add Integration split, Basic, Software, and more — then nest sub-branches under each.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog(null)}>
                        Choose branches
                    </Button>
                </Box>
            ) : (
                <PlanBranchTree
                    tree={tree}
                    definitionColors={definitionColors}
                    estimateLines={estimateLines}
                    currency={currency}
                    onAddBranches={openAddDialog}
                    onRemove={removeGroup}
                    onPatch={patchGroup}
                    onPatchItem={patchItem}
                    onAddItem={addItem}
                    onRemoveItem={removeItem}
                    onAddEstimateLine={onAddEstimateLineForGroup}
                />
            )}

            <AddBranchDialog
                open={branchDialogOpen}
                onClose={() => setBranchDialogOpen(false)}
                parentLabel={branchParent?.label}
                branches={branchOptions}
                onPick={handlePickBranch}
            />

            <GroupDefinitionsDialog open={defsOpen} onClose={() => setDefsOpen(false)} definitions={groupDefinitions} onRefresh={onRefreshDefinitions} />
        </Box>
    );
}
